import clsx from 'clsx';
import { observer } from 'mobx-react-lite';
import ProductSummary, {
  SelectedIndices,
} from '../BaseComponents/ProductSummary';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import Loader from 'components/Loader';
import media from 'assets/images/media_large.svg';
import { getProduct, getProductCategory } from '../utils';
import MobilityAttributes from './Attributes/Mobility';
import { ICartItem, useCartContext } from '../CartProvider';
import { useEffect, useState } from 'react';
import { SelectedAttributes } from './Attributes/Mobility/mobility.types';
import { postOrder } from 'api/accessexpressv3/api';
import { useAuthUser } from 'hooks/useAuthUser';
import {
  ExtraAttributesOptionType,
  ItemSubtype,
  ProductType,
  VariantsChildrenType,
} from '../accessExpress.types';
import { v4 as uuid } from 'uuid';
import { removeMatchingPrefix } from './Attributes/Mobility/Variant';
import { getPricingOptionTitle } from './Attributes/Mobility/Pricing';
import { getPlanTypeTitle } from './Attributes/Mobility/PlanType';

const ProductDetails = observer(() => {
  const { id } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const selectedState = state?.selected ?? null;
  const [selectedIndices, setSelectedIndices] = useState<SelectedIndices>({
    variantIndex: null,
    pricingOptionIndex: null,
  });
  const cart = useCartContext();
  const userInfo = useAuthUser();

  const _getProduct = () => getProduct({ id: id as string });

  useEffect(() => {
    if (cart.isOpen) cart.close();
  }, [cart]);

  const { data, isLoading } = useQuery(['products', id], _getProduct, {
    refetchOnWindowFocus: false,
  });

  const buildCartPayloads = (
    selected: SelectedAttributes,
    additional: SelectedAttributes,
    data: ProductType,
    parentId: string,
    quantity = 1,
  ) => {
    const mainItem = buildMainItem(
      selected,
      data,
      quantity,
      additional,
      parentId,
    );

    const extrasConfig = [
      { option: selected.simOption, subtype: ItemSubtype.SIM },
      {
        option: additional.simOption,
        subtype: ItemSubtype.SIM,
        isSecondary: true,
      },
      { option: selected.planTypeOption, subtype: ItemSubtype.PLAN },
      {
        option: additional.planTypeOption,
        subtype: ItemSubtype.PLAN,
        isSecondary: true,
      },
    ];

    const extras = extrasConfig
      .map(({ option, subtype, isSecondary }) =>
        buildCartExtraItem({
          option,
          subtype,
          parentId,
          isSecondary: !!isSecondary,
        }),
      )
      .filter(Boolean) as ICartItem[];

    return { mainItem, extras };
  };

  const applyItems = async (
    mode: 'update' | 'add',
    mainItem: ICartItem,
    extraItems: ICartItem[],
    selected: SelectedAttributes,
    additional: SelectedAttributes,
    data: ProductType,
    parentId: string,
  ) => {
    if (!cart.draftId) {
      const draft = await postOrder({
        additional_details: {},
        locations: [],
        customer_name: userInfo.companyName,
        requester: `${userInfo.first_name} ${userInfo.last_name}`,
        status: 'draft',
      });
      cart.updateStateFromDraft(draft);
    }

    if (mode === 'update') {
      await cart.updateItem(selectedState?.choosePricingOptionId, mainItem);

      const extrasConfigForUpdate = [
        {
          parentId,
          option: selected.simOption,
          id: selectedState?.simOptionId,
          subtype: ItemSubtype.SIM,
        },
        {
          parentId,
          option: additional.simOption,
          id: selectedState?.additionalSimOptionId,
          subtype: ItemSubtype.SIM,
          isSecondary: true,
        },
        {
          parentId,
          option: selected.planTypeOption,
          id: selectedState?.planTypeOptionId,
          subtype: ItemSubtype.PLAN,
        },
        {
          parentId,
          option: additional.planTypeOption,
          id: selectedState?.additionalPlanTypeOptionId,
          subtype: ItemSubtype.PLAN,
          isSecondary: true,
        },
      ];

      // extras
      await Promise.all(
        extrasConfigForUpdate.map(
          async ({ parentId, option, id, subtype, isSecondary }) => {
            const itemToUpdate = buildCartExtraItem({
              option,
              subtype,
              parentId,
              isSecondary,
            });
            if (itemToUpdate) {
              await cart.updateItem(id, itemToUpdate);
            } else {
              await cart.removeMobilityCartItem(String(id));
            }
          },
        ),
      );

      navigate(`../product/${data.id}`, {
        replace: true,
        state: { selected: buildSelectedState(selected, additional) },
      });
    } else if (mode === 'add') {
      await cart.addItem(mainItem, ...extraItems);
    }
  };

  const onAddToCart = async (
    selected: SelectedAttributes,
    additional: SelectedAttributes,
    update: boolean,
  ) => {
    if (!data || !selected.choosePricingOption) return;

    const parentId = uuid();
    const { mainItem, extras } = buildCartPayloads(
      selected,
      additional,
      data,
      parentId,
      update ? 0 : 1,
    );

    await applyItems(
      update ? 'update' : 'add',
      mainItem,
      extras,
      selected,
      additional,
      data,
      parentId,
    );
  };

  // --- UI states ---
  if (isLoading)
    return (
      <div className="flex flex-1 items-center justify-center">
        <Loader />
      </div>
    );

  if (!data)
    return (
      <div className="flex flex-1 grow flex-col gap-12 p-4 sm:p-8 ">
        Product not found!
      </div>
    );

  return (
    <div
      className={clsx(
        'relative flex flex-1 grow flex-col gap-12 p-4 transition-all duration-700 ease-in-out sm:p-8',
        { 'lg:-mr-[400px]': cart.isOpen },
      )}
    >
      <ProductSummary product={data} selectedIndices={selectedIndices} />
      <div className="flex flex-1 flex-col items-center justify-between lg:flex-row">
        <div className="mb-4 lg:mb-0 xl:ml-20">
          <img
            src={data?.img ?? media}
            className="object-contain md:max-h-[550px] md:max-w-[460px]"
          />
        </div>
        {data?.varients && (
          <MobilityAttributes
            productDesc={data.productDescType ?? null}
            productDetail1Desc={data.productDetail1DescType ?? null}
            variants={data.varients}
            onAddToCart={onAddToCart}
            setSelectedIndices={setSelectedIndices}
          />
        )}
      </div>
    </div>
  );
});

export default ProductDetails;

// --- Builders ---
const buildMainItem = (
  selected: SelectedAttributes,
  data: ProductType,
  quantity = 1,
  additionalSelected: SelectedAttributes,
  referenceId?: string,
) => ({
  category: 'Mobility',
  name: data.title,
  price: selected.choosePricingOption?.standardRate ?? 0,
  quantity,
  item_details: {
    ...selected.choosePricingOption,
    image: data.img,
    graniteId: data.id,
    type: 'Mobility',
    subtype: getProductCategory(
      data?.productDescType,
      data?.productDetail1DescType,
    ),
    productDescType: data?.productDescType,
    productDetail1DescType: data?.productDetail1DescType,
    referenceId,
    global: selected.global,
    dataPlanNeeded: selected.dataPlanNeeded,
    planTypeOption: selected.planTypeOption,
    simOption: selected.simOption,
    keyFeature: removeMatchingPrefix(
      selected.chooseVariant?.productAlias ?? '',
      selected.chooseVariant?.productCodeDescription ?? '',
    ),
    pricingOption: getPricingOptionTitle({
      contractTerm_Months: selected.choosePricingOption?.contractTerm_Months,
      productGroupDesc: selected.choosePricingOption?.productGroupDesc,
    } as VariantsChildrenType),
    planType: selected.planType?.connection_type
      ? getPlanTypeTitle(selected.planType.connection_type)
      : undefined,
    simType: selected.simType?.sim_type,
    carrierName: selected?.carrier?.carrier_name,
    // secondary attributes
    additionalGlobal: additionalSelected.global,
    additionalDataPlanNeeded: additionalSelected.dataPlanNeeded,
    additionalPlanTypeOption: additionalSelected.planTypeOption,
    additionalSimOption: additionalSelected.simOption,
    additionalPlanType: additionalSelected.planType?.connection_type
      ? getPlanTypeTitle(additionalSelected.planType.connection_type)
      : undefined,
    additionalCarrierName: additionalSelected?.carrier?.carrier_name,
  },
  subtype: getProductCategory(
    data?.productDescType,
    data?.productDetail1DescType,
  ),
});

const buildCartExtraItem = ({
  option,
  subtype,
  parentId,
  isSecondary,
}: {
  option?: ExtraAttributesOptionType | null;
  subtype: ItemSubtype;
  parentId: string;
  isSecondary?: boolean;
}) => {
  if (!option) return null;
  return {
    category: 'Mobility',
    name: option.product_alias ?? option.description ?? '',
    price: option.standard_rate ?? 0,
    quantity: 1,
    item_details: {
      ...option,
      graniteId: option.product_alias ?? option.description ?? '',
      type: 'Mobility',
      subtype,
      parentId,
      ...(isSecondary ? { secondaryPlan: true } : {}),
    },
    subtype,
  } as ICartItem;
};

const buildSelectedState = (
  selected: SelectedAttributes,
  additional: SelectedAttributes,
) => ({
  globalOrderCarrier: selected?.global?.orderCarrier.join(',') || '',
  globalCoverage: selected?.global?.coverage,
  dataPlanNeededActivation: selected?.dataPlanNeeded?.activation,
  choosePricingOptionId: selected.choosePricingOption?.pimProductPricingId,
  simOptionId: selected.simOption?.pim_product_pricing_id ?? null,
  planTypeOptionId: selected.planTypeOption?.pim_product_pricing_id ?? null,
  additionalSimOptionId: additional?.simOption?.pim_product_pricing_id ?? null,
  additionalPlanTypeOptionId:
    additional?.planTypeOption?.pim_product_pricing_id ?? null,
});
